import { Toast } from 'primereact/toast';
import React, { useEffect, useRef } from 'react';

import networkToastService from './networkToastService';
import './NetworkStatus.scss';

/** Delay (ms) before checking initial offline state — lets PrimeReact
 *  Toast finish initialising its internal DOM before show() is called. */
const TOAST_INIT_CHECK_DELAY_MS = 50;

/**
 * Registers the Toast ref with networkToastService AND wires browser
 * online/offline events so the toast fires immediately on WiFi drop —
 * without waiting for an API call to fail.
 *
 * apiSlice also calls networkToastService on FETCH_ERROR as a backstop
 * for VPN drops where browser events may not fire.
 */
export const NetworkStatus: React.FC = () => {
    const toastRef = useRef<Toast>(null);

    useEffect(() => {
        // Register ref — must happen before any show/clear calls
        networkToastService.setRef(toastRef.current);

        const handleOffline = () => networkToastService.showOffline();
        const handleOnline = () => networkToastService.showOnline();

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        // Check state on mount — setTimeout lets PrimeReact Toast finish
        // initialising its internal DOM before we call show()
        if (!navigator.onLine) {
            setTimeout(() => networkToastService.showOffline(), TOAST_INIT_CHECK_DELAY_MS);
        }

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
            networkToastService.isOffline = false;
            networkToastService.setRef(null);
        };
    }, []);

    return (
        <Toast
            ref={toastRef}
            position="top-center"
            className="nw-status-toast"
        />
    );
};
